// Modo Telão: regras puras (sem Vue) para a TV da clínica mostrar, por profissional,
// quem está sendo atendido agora e quem vem a seguir.

const PARTICULAS = new Set(['de', 'da', 'do', 'das', 'dos', 'e', 'di', 'du']);

// Bloqueios de agenda que não são atendimento ("Fechada", "Agenda fechada"...).
const BLOQUEIO =
  /^\s*(fechad[oa]|agenda\b|bloquead[oa]|bloqueio|f[ée]rias|feriado|almo[cç]o)/i;

// Acima disso não é sessão, é bloqueio do dia (há eventos de 12 h nas agendas da CIES).
const DURACAO_MAXIMA_MIN = 4 * 60;

const capitalizar = palavra =>
  palavra
    ? palavra.charAt(0).toLocaleUpperCase('pt-BR') +
      palavra.slice(1).toLocaleLowerCase('pt-BR')
    : '';

// LGPD: a TV fica à vista de várias pessoas, então a criança aparece como
// "primeiro nome + inicial do sobrenome" — "DAVI MONTEIRO DE OLIVEIRA" vira "Davi M.".
export function abreviarPaciente(nome) {
  const palavras = String(nome || '')
    .replace(/[^\p{L}\s'-]/gu, ' ')
    .split(/\s+/)
    .filter(Boolean);
  if (!palavras.length) return '';
  const primeiro = capitalizar(palavras[0]);
  const sobrenome = palavras
    .slice(1)
    .find(p => !PARTICULAS.has(p.toLowerCase()));
  return sobrenome
    ? `${primeiro} ${sobrenome.charAt(0).toLocaleUpperCase('pt-BR')}.`
    : primeiro;
}

// A agenda principal compartilhada por cada profissional chega com o e-mail como nome
// enquanto a clínica não a renomeia no Google. Do e-mail tira-se algo legível.
export function nomeDaProfissional(nomeAgenda) {
  const nome = String(nomeAgenda || '').trim();
  if (!nome.includes('@')) return nome;
  const local = nome.split('@')[0].replace(/\d+/g, ' ');
  return local
    .split(/[._\-\s]+/)
    .filter(Boolean)
    .map(capitalizar)
    .join(' ');
}

const instante = valor => {
  if (!valor) return null;
  const texto = typeof valor === 'string' ? valor : valor.dateTime;
  if (!texto) return null; // evento de dia inteiro ({ date }) não é sessão
  const data = new Date(texto);
  return Number.isNaN(data.getTime()) ? null : data;
};

export function ehAtendimento(evento) {
  const inicio = instante(evento.start);
  const fim = instante(evento.end);
  if (!inicio || !fim || fim <= inicio) return false;
  if ((fim - inicio) / 60000 > DURACAO_MAXIMA_MIN) return false;
  const titulo = String(evento.summary || '').trim();
  return Boolean(titulo) && !BLOQUEIO.test(titulo);
}

const atendimento = evento => ({
  id: evento.id,
  paciente: abreviarPaciente(evento.summary),
  inicio: instante(evento.start),
  fim: instante(evento.end),
});

// Profissional livre só aparece se o próximo atendimento começa logo; quem só volta
// à tarde não precisa ocupar a TV de manhã.
const JANELA_PROFISSIONAL_LIVRE_MIN = 60;

const juntarNomes = nomes =>
  nomes.length > 1
    ? `${nomes.slice(0, -1).join(', ')} e ${nomes[nomes.length - 1]}`
    : nomes[0] || '';

// Dois atendimentos ao mesmo tempo com a mesma profissional (acontece em grupo) viram
// uma linha só: "Heitor C. e Paloma", das 09:40 às 10:20.
const emAndamento = lista =>
  lista.length
    ? {
        id: lista.map(e => e.id).join('+'),
        paciente: juntarNomes(lista.map(e => e.paciente)),
        inicio: new Date(Math.min(...lista.map(e => e.inicio))),
        fim: new Date(Math.max(...lista.map(e => e.fim))),
      }
    : null;

// Um cartão por profissional: o atendimento em andamento e o próximo a começar.
// Quem está atendendo vem primeiro; depois, quem começa mais cedo.
export function montarCartoes(eventos, agora = new Date()) {
  const porAgenda = new Map();
  eventos.filter(ehAtendimento).forEach(evento => {
    const chave = evento.calendarId;
    if (!porAgenda.has(chave)) {
      porAgenda.set(chave, {
        agendaId: chave,
        profissional: nomeDaProfissional(evento.calendarName),
        cor: evento.calendarColor,
        eventos: [],
      });
    }
    porAgenda.get(chave).eventos.push(atendimento(evento));
  });

  const limiteLivre = agora.getTime() + JANELA_PROFISSIONAL_LIVRE_MIN * 60000;
  const cartoes = [];
  porAgenda.forEach(agenda => {
    const ordenados = agenda.eventos.sort((a, b) => a.inicio - b.inicio);
    const atual = emAndamento(
      ordenados.filter(e => e.inicio <= agora && agora < e.fim)
    );
    const proximo = ordenados.find(e => e.inicio > agora) || null;
    if (!atual && (!proximo || proximo.inicio.getTime() > limiteLivre)) return;
    cartoes.push({
      agendaId: agenda.agendaId,
      profissional: agenda.profissional,
      cor: agenda.cor,
      atual,
      proximo,
    });
  });

  return cartoes.sort((a, b) => {
    if (a.atual && !b.atual) return -1;
    if (!a.atual && b.atual) return 1;
    const ia = (a.atual || a.proximo).inicio;
    const ib = (b.atual || b.proximo).inicio;
    return ia - ib || a.profissional.localeCompare(b.profissional, 'pt-BR');
  });
}

export function paginar(lista, tamanho) {
  if (!lista.length) return [[]];
  const paginas = [];
  for (let i = 0; i < lista.length; i += tamanho)
    paginas.push(lista.slice(i, i + tamanho));
  return paginas;
}

// Colunas da grade pelo número de cartões na página: poucos cartões ficam enormes.
export function colunasPara(quantidade) {
  if (quantidade <= 1) return 1;
  if (quantidade <= 4) return 2;
  if (quantidade <= 9) return 3;
  return 4;
}

export const formatarHora = data =>
  data
    ? data.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
    : '';
